import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Configuraciones = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // WhatsApp Context Retention
  const [retentionDays, setRetentionDays] = useState(15);
  // WhatsApp Messages Retention
  const [msgRetentionDays, setMsgRetentionDays] = useState(30);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Usar Promise.allSettled para que si una falla, la otra pueda cargar
      const [resContext, resMsg] = await Promise.allSettled([
        axios.get(`${API}/configuraciones/whatsapp_context_retention_days`),
        axios.get(`${API}/configuraciones/whatsapp_messages_retention_days`)
      ]);

      if (resContext.status === 'fulfilled' && resContext.value.data?.valor) {
        setRetentionDays(resContext.value.data.valor);
      } else {
        setRetentionDays(15);
      }

      if (resMsg.status === 'fulfilled' && resMsg.value.data?.valor) {
        setMsgRetentionDays(resMsg.value.data.valor);
      } else {
        setMsgRetentionDays(30);
      }

    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      toast.error('Error al cargar configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRetention = async () => {
    if (retentionDays < 1 || msgRetentionDays < 1) {
      toast.error('La cantidad de días debe ser al menos 1');
      return;
    }
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/configuraciones/whatsapp_context_retention_days`, {
          valor: parseFloat(retentionDays)
        }),
        axios.put(`${API}/configuraciones/whatsapp_messages_retention_days`, {
          valor: parseFloat(msgRetentionDays)
        })
      ]);
      toast.success('Políticas de retención actualizadas');
    } catch (error) {
      console.error('Error guardando configuración:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (user?.rol !== 'SuperAdmin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">No autorizado</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Configuraciones Generales</h1>
              <p className="text-muted-foreground mt-1">Ajustes globales del sistema (Solo SuperAdmin)</p>
            </div>
            <Button onClick={handleSaveRetention} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          
          {/* Card WhatsApp Context */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Retención de Contexto de WhatsApp
              </CardTitle>
              <CardDescription>
                Configura cuántos días se mantendrá el historial de contexto del bot de WhatsApp.
                Los contextos más antiguos se eliminarán automáticamente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="retentionDays">Cantidad de días (Contexto)</Label>
                <Input
                  id="retentionDays"
                  type="number"
                  min="1"
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Recomendado: 15 días.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card WhatsApp Messages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Retención de Mensajes de WhatsApp
              </CardTitle>
              <CardDescription>
                Configura cuántos días se mantendrá el historial de mensajes reales de WhatsApp en la base de datos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="msgRetentionDays">Cantidad de días (Mensajes)</Label>
                <Input
                  id="msgRetentionDays"
                  type="number"
                  min="1"
                  value={msgRetentionDays}
                  onChange={(e) => setMsgRetentionDays(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Recomendado: 30 días.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Configuraciones;
